/**
 * 表セルの回答内容を、PDF 描画用のデータに組み立てる。
 *
 * 画面側の TableSection/buildAnsweredCell と同じ規則だが、あちらは MUI の ReactNode
 * （署名は img 要素）を返すため @react-pdf/renderer ではそのまま使えない。ここでは描画方法に
 * 依存しないデータ（テキスト or 署名 URL）を返し、描画は呼び出し側に委ねる。
 *
 * 写真は枚数テキスト（「N枚」）で表す。PDF の極小セルに複数画像を敷き詰めるのは非現実的なため、
 * grid レイアウトPDF（TableBodyCellResultImage）と同じ扱いに揃えている。
 */
import { gray } from '@kmns-internal/furikake/tokens'
import {
  ReportQuestion,
  ResponseDatetimeSubTypeEnum,
  ResponseTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { convertDecimal } from 'src/exShared/util/numberDecimalPoint/numberDecimalPoint'
import { formatMinutes } from 'src/exShared/util/report/formatMinutes'
import { formatDatetimeValue } from 'src/features/reports/result/components/reportResult/datetimeFormatter.util'
import { multipleChoiceColors } from 'src/features/theme/MultipleChoiceColor'
import { ExhaustiveError } from 'src/util/error'

export type CellContent =
  | { kind: 'text'; text: string; isInvalid: boolean; fontColor?: string }
  | { kind: 'signature'; url: string }
  | { kind: 'empty' }

/** 選択肢の設定色が白orグレーの場合、可視性を高めるためグレー文字にフォールバックする。 */
const getMultipleChoiceFontColor = (color?: string): string | undefined => {
  if (color === undefined) return undefined
  if (color === '' || color === multipleChoiceColors.default) return gray[6]
  return color
}

/**
 * 回答済みセルの描画内容を回答形式ごとに組み立てる。表の列になりうる全形式を網羅し、
 * 列にならない形式（INFORMATION 等）も明示して空セルにフォールバックする。
 * ResponseTypeEnum に新しい値が増えたら default の ExhaustiveError がコンパイルエラーになる。
 */
export const buildCellContent = (
  question: ReportQuestion,
  responseType: ResponseTypeEnum,
): CellContent => {
  const answer = question.responseAnswer
  switch (responseType) {
    case ResponseTypeEnum.NUMBER: {
      const numberValue = answer?.numberValue
      const value =
        numberValue?.numberValue !== undefined
          ? convertDecimal(
              numberValue.numberValue,
              question.responseNumbers?.[0]?.decimalPoint,
            )
          : ''
      return {
        kind: 'text',
        text: `${value}${question.scale ?? ''}`,
        isInvalid: numberValue?.isInvalid === 1,
      }
    }
    case ResponseTypeEnum.TEXT:
      return {
        kind: 'text',
        text: answer?.textValue?.textValue ?? '',
        isInvalid: false,
      }
    case ResponseTypeEnum.DATETIME: {
      const datetimeValue = answer?.datetimeValue
      return {
        kind: 'text',
        text: datetimeValue?.datetimeValue
          ? formatDatetimeValue(
              question.responseDatetimes?.[0]?.subType ??
                ResponseDatetimeSubTypeEnum.DATETIME,
              datetimeValue.datetimeValue,
            )
          : '',
        isInvalid: datetimeValue?.isInvalid === 1,
      }
    }
    case ResponseTypeEnum.MULTIPLE_CHOICE: {
      const multipleChoice = answer?.multipleChoice
      const isInvalid = multipleChoice?.isInvalid === 1
      return {
        kind: 'text',
        text: multipleChoice?.response ?? '',
        isInvalid,
        fontColor: isInvalid
          ? undefined
          : getMultipleChoiceFontColor(multipleChoice?.color),
      }
    }
    case ResponseTypeEnum.RESPONSE_SET:
      return {
        kind: 'text',
        text: (answer?.responseSets ?? []).map(set => set.name).join('、'),
        isInvalid: false,
      }
    case ResponseTypeEnum.EMPLOYEE:
      return {
        kind: 'text',
        text: (answer?.employees ?? [])
          .map(employee => employee.name)
          .join('、'),
        isInvalid: false,
      }
    case ResponseTypeEnum.FORMULA: {
      const formulaValue = answer?.formulaValue
      const value =
        formulaValue?.formulaValue !== undefined
          ? convertDecimal(
              formulaValue.formulaValue,
              question.responseFormulas?.[0]?.decimalPoint,
            )
          : ''
      return {
        kind: 'text',
        text: `${value}${question.scale ?? ''}`,
        isInvalid: formulaValue?.isInvalid === 1,
      }
    }
    case ResponseTypeEnum.TIME_MEASUREMENT: {
      const timeMeasurement = answer?.timeMeasurementValue
      return {
        kind: 'text',
        text:
          timeMeasurement?.value !== undefined
            ? formatMinutes(timeMeasurement.value)
            : '',
        isInvalid: timeMeasurement?.isInvalid === true,
      }
    }
    case ResponseTypeEnum.SIGNATURE: {
      const signature = answer?.signatures?.[0]
      return signature?.url
        ? { kind: 'signature', url: signature.url }
        : { kind: 'empty' }
    }
    case ResponseTypeEnum.RESULT_IMAGE:
      // PDF では枚数テキストで表す（grid レイアウトPDF と同じ）。
      return {
        kind: 'text',
        text: `${(answer?.resultImages ?? []).length}枚`,
        isInvalid: false,
      }
    case ResponseTypeEnum.INFORMATION:
    case ResponseTypeEnum.GRID_VARIABLE:
    case ResponseTypeEnum.URL:
    case ResponseTypeEnum.LABEL_CHECK:
      // 表の列の回答形式として選べない形式（columnType.tsx の COLUMN_INPUT_TYPE 外）。
      // 万一データに現れても表では空セルにする。
      return { kind: 'empty' }
    default:
      throw new ExhaustiveError(responseType)
  }
}
